// Home.tsx - accueil de la landing (refonte « allégée » du 6 septembre 2026).
//
// Quatre temps, pas un de plus - la version précédente en comptait cinq, avec
// un tableau des possibilités et une section « qui sommes-nous » qui noyaient
// le message :
//   1. HERO - la promesse + les deux publics (« je cherche » / « je propose ») ;
//   2. COMMUNAUTÉ (#communaute) - toutes les tables d'une ville au même
//      endroit, le parcours en trois temps, puis les DEUX FAÇONS DE RÉSERVER
//      vues par le gourmet (dernière minute / à l'avance) ;
//   3. LOGICIEL (#logiciel) - les deux colonnes du DÉPLIANT restaurateur
//      (leaflet v2, 6 septembre 2026) : « Simplifiez votre quotidien » et
//      « Développez votre visibilité », texte repris au mot près ;
//   4. OFFRE (#offre) - une seule offre, six lignes, un e-mail.
//
// Le formulaire de contact a été RETIRÉ (6 septembre 2026) : un restaurateur
// écrit à `SUPPORT_EMAIL`, la même adresse que celle affichée dans la PWA.
import type { ReactElement, ReactNode } from "react";
import { BadgeAppStore, BadgeGooglePlay, CyclingWord, Icon, type IconName } from "@hop-resto/ui";
import { APP_STORE_URL, CONTACT_EMAIL, CONTACT_MAILTO, PLAY_STORE_URL } from "../lib/constants";
import { Partenaires } from "./Partenaires";
import type { Partenaire } from "../lib/partenaires";

interface HomeProps {
  cityNames: string[];
  /**
   * Restaurants cochés « vitrine landing » par l'admin
   * (docs/rules/vitrine-partenaires-landing.md). Liste vide = la section
   * partenaires ne s'affiche pas du tout.
   */
  partenaires: Partenaire[];
}

/**
 * Les deux façons de réserver, reprises du dépliant mais RETOURNÉES côté
 * gourmet : le papier s'adresse au restaurateur (« Signalez une table »,
 * « c'est vous qui choisissez »), la landing s'adresse ici au client. Même
 * promesse, autre point de vue.
 */
const MODES = [
  {
    icon: "clock" as IconName,
    t: "Réservation de dernière minute",
    d: (
      <>
        Un restaurant <em className="hl">signale une table libre</em>, vous êtes{" "}
        <em className="hl">prévenu aussitôt</em>. Vous demandez en un geste, il valide : c'est
        réservé.
      </>
    ),
  },
  {
    icon: "calendar" as IconName,
    t: "Réservation à l'avance",
    d: (
      <>
        Choisissez votre date directement sur l'application,{" "}
        <em className="hl">15, 30 ou 60 jours à l'avance</em> selon le restaurant. Toujours{" "}
        <em className="hl">sans créer de compte</em>.
      </>
    ),
  },
] as const;

/**
 * Les deux colonnes du dépliant restaurateur (« Bien plus qu'un simple outil
 * de réservation ») - texte repris au mot près du leaflet v2. Les verbes
 * surlignés (`hl`) sont ceux mis en couleur sur le papier : au plus deux ou
 * trois par bloc, sinon le surlignage ne surligne plus rien.
 *
 * Les glyphes du dépliant (mégaphone, case à cocher, immeubles) n'existent pas
 * dans le design system partagé, qu'on ne modifie pas : on prend le plus proche
 * par le SENS, pas par le dessin.
 */
const QUOTIDIEN = [
  {
    icon: "map" as IconName,
    t: "Plan de salle",
    d: (
      <>
        <em className="hl">Paramétrez</em> votre restaurant et{" "}
        <em className="hl">visualisez vos tables et vos réservations</em> en un coup d'œil.
      </>
    ),
  },
  {
    icon: "refresh" as IconName,
    t: "Deux modes",
    d: (
      <>
        Basculez du mode 'Administration' au mode 'Salle' pour ne garder{" "}
        <em className="hl">que l'essentiel en salle</em> !
      </>
    ),
  },
  {
    icon: "edit" as IconName,
    t: "Un accueil plus fluide",
    d: (
      <>
        <em className="hl">Modifiez</em> facilement les réservations et laissez vos clients les
        ajuster jusqu'à 24 h avant. <em className="hl">Automatisez</em> les confirmations et les
        rappels, puis <em className="hl">validez</em> les arrivées et installez vos clients en un
        geste.
      </>
    ),
  },
  {
    // Pas d'icône « graphique » dans le design system partagé : glyphe inline,
    // exactement comme la PWA restaurateur.
    icon: "chart" as const,
    t: "Statistiques & prévisions",
    d: (
      <>
        <em className="hl">Suivez</em> votre activité et <em className="hl">anticipez</em> plus
        facilement les prochains services.
      </>
    ),
  },
] as const;

const VISIBILITE = [
  {
    icon: "photo" as IconName,
    t: "Partagez vos actualités",
    d: (
      <>
        <em className="hl">Publiez en story</em> vos menus du moment, promotions, événements et
        nouveautés auprès de la communauté Hop Resto.
      </>
    ),
  },
  {
    icon: "search" as IconName,
    t: "Présence en ligne",
    d: (
      <>
        Profitez d'une <em className="hl">page web dédiée</em> à votre restaurant et d'un{" "}
        <em className="hl">widget de disponibilités</em> à intégrer directement sur votre site.
      </>
    ),
  },
  {
    icon: "pin" as IconName,
    t: "Une dynamique locale et collective",
    // Le seul bloc sur fond indigo du dépliant : il parle du réseau, pas du
    // logiciel. On garde sa mise en avant.
    fond: true,
    d: (
      <>
        Hop Resto <em className="hl">rassemble les restaurants et les habitants</em> d'un même
        territoire. Plus le réseau grandit, plus chacun bénéficie de nouvelles opportunités.
      </>
    ),
  },
] as const;

/** Ce que l'offre unique contient - six lignes, pas une de plus. */
const OFFRE_POINTS = [
  "Tables de dernière minute,",
  "Réservations à l'avance jusqu'à 60 jours,",
  "Plan de salle 2D,",
  "Page web dédiée et widget pour votre site,",
  "Story photo,",
  "Statistiques et prévisions,",
  "... et bien plus !",
] as const;

export function Home({ cityNames, partenaires }: HomeProps): ReactElement {
  return (
    <main>
      {/* ── Hero (photo plein cadre) ─────────────────────────── */}
      <section className="hero">
        <div className="hero-bg" aria-hidden="true">
          <img
            className="hero-img"
            src="/hero-1600.webp"
            srcSet="/hero-960.webp 960w, /hero-1600.webp 1600w"
            sizes="100vw"
            width={1672}
            height={941}
            alt=""
            loading="eager"
          />
          <div className="hero-overlay" />
        </div>

        <div className="hero-inner container">
          <p className="live-pill live-pill--dark">
            <span className="live-dot hr-pulse" aria-hidden="true" />
            <span className="live-text">
              En direct, à{" "}
              <strong>
                <CyclingWord words={[...cityNames, "votre ville ?"]} color="#fff" />
              </strong>
            </span>
          </p>

          <h1 className="hero-title">
            Toutes vos réservations
            <br />
            dans votre poche !
          </h1>

          <p className="hero-lede">
            Hop Resto rassemble les restaurants et les habitants d'un même territoire.
          </p>

          <div className="split">
            <SplitCard
              icon="search"
              accent="var(--coral)"
              kicker="Gourmets"
              title="Je cherche une table"
              desc="Les tables libres autour de vous, en direct. Gratuit, sans compte."
            >
              {/* App publiée le 21 septembre 2026 : les badges des stores, un
                  par plateforme, plutôt qu'un bouton unique qui devinerait
                  laquelle - une détection ratée enverrait le visiteur sur le
                  mauvais store. Mêmes badges que la page web restaurateur
                  (@hop-resto/ui), en plus grand. */}
              <div className="split-stores">
                <BadgeAppStore href={APP_STORE_URL} hauteur={50} />
                <BadgeGooglePlay href={PLAY_STORE_URL} hauteur={50} />
              </div>
              <p className="split-sub">Gratuite, sans compte</p>
            </SplitCard>

            <SplitCard
              icon="plate"
              accent="var(--ink)"
              kicker="Restaurateurs"
              title="Je propose une table"
              desc="Votre logiciel de réservation, et toute une ville qui vous découvre."
            >
              <a className="btn btn-primary btn-block" href={CONTACT_MAILTO}>
                Nous écrire
              </a>
              <p className="split-sub">{CONTACT_EMAIL}</p>
            </SplitCard>
          </div>
        </div>
      </section>

      {/* ── Les restaurants déjà partenaires ─────────────────────
          Juste sous les deux cartes, mais HORS du hero : posée sur le crème
          plutôt que sur la photo, la rangée de logos reste lisible. Ne rend
          rien tant qu'aucun resto n'est coché côté admin
          (docs/rules/vitrine-partenaires-landing.md). */}
      <Partenaires partenaires={partenaires} />

      {/* ── La communauté ────────────────────────────────────── */}
      <section id="communaute" className="how container">
        <p className="eyebrow">Toutes les tables de la ville, au même endroit.</p>
        <p className="section-lede">
          Réservation unifiée : Une seule application pour les restaurants d'une même ville, plus besoin de jongler entre mail, téléphone, site web, ... .
        </p>


        {/* Les deux modes de réservation, vus par le gourmet : c'est ici que se
            joue la « facilité de réservation », pas dans le parcours ci-dessus
            qui raconte le réseau. */}
        <div className="modes">
          <h3 className="modes-title">Deux façons de réserver, aussi simples l'une que l'autre.</h3>
          <div className="modes-grid">
            {MODES.map((m) => (
              <article key={m.t} className="f-row">
                <span className="f-icon" aria-hidden="true">
                  <Icon name={m.icon} size={21} stroke={2.2} color="var(--coral)" />
                </span>
                <div>
                  <h4>{m.t}</h4>
                  <p>{m.d}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Le logiciel : les deux colonnes du dépliant ──────── */}
      <section id="logiciel" className="how container">
        <p className="eyebrow">Bien plus qu'un simple outil de réservation.</p>
        <p className="section-lede">
          Pilotez tout votre service - sans matériel à acheter, directement sur votre ordinateur,
          tablette ou téléphone.
        </p>

        <div className="cols">
          <Colonne titre="Simplifiez votre quotidien" ton="coral" blocs={QUOTIDIEN} />
          <Colonne titre="Développez votre visibilité" ton="indigo" blocs={VISIBILITE} />
        </div>
      </section>

      {/* ── Offre UNIQUE ─────────────────────────────────────── */}
      <section id="offre" className="how container">
        <p className="eyebrow">Une seule offre.</p>

        <div className="offer-solo">
          <div className="offer-solo-head">
            <p className="offer-solo-pitch">
              Pas de module, pas de version bridée : vous activez ce dont vous avez besoin.
            </p>
          </div>
          <ul className="offer-points offer-points--two" role="list">
            {OFFRE_POINTS.map((p) => (
              <li key={p}>
                <Icon name="check" size={15} stroke={2.8} color="var(--coral)" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <a className="btn btn-primary btn-lg" href={CONTACT_MAILTO}>
            Nous écrire
          </a>
          <p className="split-sub">
            {CONTACT_EMAIL} - réponse sous 48 h.
          </p>
        </div>
      </section>
    </main>
  );
}

interface Bloc {
  readonly icon: IconName | "chart";
  readonly t: string;
  readonly d: ReactNode;
  readonly fond?: boolean;
}

/**
 * Une colonne du dépliant : un intitulé coloré, puis des blocs icône + texte.
 * Le `ton` ne colore QUE l'intitulé - le corps du texte reste à l'encre, et
 * les glyphes restent corail, comme sur le papier. Seul le bloc « réseau »,
 * posé sur son fond teinté, passe à l'indigo.
 */
function Colonne({
  titre,
  ton,
  blocs,
}: {
  titre: string;
  ton: "coral" | "indigo";
  blocs: readonly Bloc[];
}): ReactElement {
  return (
    <section className={`col col--${ton}`}>
      <h3 className="col-title">{titre}</h3>
      {blocs.map((b) => {
        const teinte = b.fond ? "var(--indigo)" : "var(--coral)";
        return (
          <article key={b.t} className={b.fond ? "f-row f-row--fond" : "f-row"}>
            <span className="f-icon" aria-hidden="true">
              {b.icon === "chart" ? (
                <ChartGlyph color={teinte} />
              ) : (
                <Icon name={b.icon} size={21} stroke={2.2} color={teinte} />
              )}
            </span>
            <div>
              <h4>{b.t}</h4>
              <p>{b.d}</p>
            </div>
          </article>
        );
      })}
    </section>
  );
}

interface SplitCardProps {
  icon: IconName;
  accent: string;
  kicker: string;
  title: string;
  desc: string;
  children: ReactNode;
}

function SplitCard({ icon, accent, kicker, title, desc, children }: SplitCardProps): ReactElement {
  return (
    <div className="split-card">
      <span className="split-icon" style={{ color: accent }}>
        <Icon name={icon} size={26} stroke={2.2} color={accent} />
      </span>
      <p className="split-kicker">{kicker}</p>
      <h2 className="split-title">{title}</h2>
      <p className="split-desc">{desc}</p>
      {children}
    </div>
  );
}

/**
 * Icône « statistiques » : histogramme. Inline plutôt qu'ajoutée au design
 * system partagé (`packages/ui`, qu'on ne modifie pas) - même précédent que
 * la PWA restaurateur, qui porte le sien pour la même raison.
 */
function ChartGlyph({ color = "var(--coral)" }: { color?: string }): ReactElement {
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  );
}
